import React, { useEffect, useState } from "react";
import { Flex, Button, Title, TextInput, Text } from "@tremor/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as profileAPI from "../../services/profiles";
import * as sourcesAPI from "../../services/sources";
import { Link } from "react-router-dom";
import IconPicker from "../../components/IconPicker";

import { profileSchema } from "./schema";

export default function ProfileForm({ onCancel, profile }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [icon, setIcon] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(profileSchema),
  });

  const onSubmitEvent = async (data) => {
    if (!data.source) {
      return;
    }
    try {
      setLoading(true);
      const payload = { ...data };
      if (icon) {
        payload.image = icon;
      }
      await profileAPI.create(payload);
      reset({ name: "", source: "" });
      setIcon("");
      onCancel();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  };

  const getSources = async () => {
    try {
      const response = await sourcesAPI.list(1, 25);
      setSources(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getSources();
  }, []);

  return (
    <div>
      <Flex className="pb-2 mb-2">
        <Title>{"Create Profile"}</Title>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 dark:text-gray-500 bg-transparent hover:bg-gray-200 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
        >
          <XMarkIcon className="h-5" />
          <span className="sr-only">Close modal</span>
        </button>
      </Flex>
      <Text></Text>
      <div className="form-box mt-6 mb-6">
        <form onSubmit={handleSubmit(onSubmitEvent)}>
          <div className="form-group mb-6">
            <label
              htmlFor="name"
              className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2"
            >
              Name <span className="text-red-700 dark:text-red-400">*</span>
            </label>
            <TextInput
              placeholder="Dragino LHT65"
              error={errors.name}
              errorMessage={errors.name?.message}
              value={profile?.name}
              type="text"
              {...register("name")}
            />
          </div>

          <div className="form-group">
            <label
              htmlFor="provider"
              className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2"
            >
              Pick Source <span className="text-red-700 dark:text-red-400">*</span>
            </label>
            <select
              name="source"
              className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-50 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              {...register("source", { required: true })}
            >
              <option value="">Select a source</option>
              {sources.map((source) => (
                <option key={source._id} value={source._id}>
                  {source.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-tremor-default text-tremor-content dark:text-dark-tremor-content">
              Don't see a source?{" "}
              <Link to="/sources" className="underline underline-offset-4">
                Create One
              </Link>
            </p>
          </div>

          <div className="form-group mt-6">
            <label
              htmlFor="icon"
              className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2"
            >
              Icon (optional)
            </label>
            <IconPicker value={icon} onChange={setIcon} />
          </div>

          <div className="footer mt-10">
            <Flex justifyContent="center">
              <Button
                variant="primary"
                type="submit"
                className="w-full"
                loading={loading}
              >
                Create Profile
              </Button>
            </Flex>
          </div>
        </form>
      </div>
    </div>
  );
}
